<template>
  <div class="quality-page">
    <!-- 导航栏 -->
    <WebsiteNavbar />

    <div class="hero-section">
      <div class="hero-background">
        <img :src="heroBackgroundImage" alt="Quality Background" />
        <div class="slide-overlay"></div>
      </div>
      <div class="container">
        <h1 class="page-title">质量保证</h1>
        <p class="page-subtitle">严格的质量控制体系，确保产品卓越品质</p>
      </div>
    </div>
    
    <div class="content-section">
      <div class="container">
        <div class="quality-intro">
          <h2 class="section-title">质量管理体系</h2>
          <p class="section-description">
            我们建立了完善的质量管理体系，从原材料采购到成品出厂，每一个环节都严格把控，
            确保产品符合国际标准和客户要求。
          </p>
        </div>
        
        <div class="quality-grid">
          <div
            class="quality-card"
            v-for="item in qualityItems"
            :key="item.id"
            :style="{ backgroundImage: `url(${item.backgroundImage})` }"
          >
            <div class="card-overlay"></div>
            <div class="card-content">
              <div class="quality-icon">
                <img :src="item.icon" :alt="item.title" />
              </div>
              <h3 class="quality-title">{{ item.title }}</h3>
              <p class="quality-description">{{ item.description }}</p>
            </div>
          </div>
        </div>
        
        <div class="certifications">
          <h2 class="section-title">资质认证</h2>
          <div class="cert-grid">
            <div class="cert-item" v-for="cert in certifications" :key="cert.id">
              <div class="cert-icon">
                <img :src="cert.icon" :alt="cert.name" />
              </div>
              <h4 class="cert-name">{{ cert.name }}</h4>
              <p class="cert-description">{{ cert.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 页脚 -->
    <WebsiteFooter />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import WebsiteNavbar from './components/WebsiteNavbar.vue'
import WebsiteFooter from './components/WebsiteFooter.vue'

const heroBackgroundImage = new URL('@/assets/carousel/slide1.webp', import.meta.url).href

interface QualityItem {
  id: number
  title: string
  description: string
  icon: string
  backgroundImage: string
}

interface Certification {
  id: number
  name: string
  description: string
  icon: string
}

const qualityItems = ref<QualityItem[]>([
  {
    id: 1,
    title: "原材料检验",
    description: "严格的原材料检验流程，确保所有材料符合质量标准，从源头保证产品质量。",
    icon: "/src/assets/assets/svg/service.svg",
    backgroundImage: new URL('@/assets/products-center/Pneumatic Accessories.png', import.meta.url).href
  },
  {
    id: 2,
    title: "生产过程控制",
    description: "全流程质量监控，实时检测生产过程中的各项参数，确保产品一致性。",
    icon: "/src/assets/assets/svg/system.svg",
    backgroundImage: new URL('@/assets/products-center/Machine Parts.png', import.meta.url).href
  },
  {
    id: 3,
    title: "成品检测",
    description: "全面的成品检测体系，包括性能测试、耐久性测试等多项检测项目。",
    icon: "/src/assets/assets/svg/laptop.svg",
    backgroundImage: new URL('@/assets/products-center/Pneumatic Controllers.png', import.meta.url).href
  },
  {
    id: 4,
    title: "持续改进",
    description: "建立持续改进机制，不断优化生产工艺和质量控制流程。",
    icon: "/src/assets/assets/svg/globalization.svg",
    backgroundImage: new URL('@/assets/products-center/E-Motion products.png', import.meta.url).href
  }
])

const certifications = ref<Certification[]>([
  {
    id: 1,
    name: "ISO 9001",
    description: "国际质量管理体系认证，确保产品质量管理水平达到国际标准。",
    icon: "/src/assets/assets/svg/hot.svg"
  },
  {
    id: 2,
    name: "CE认证",
    description: "欧盟安全认证，产品符合欧盟安全、健康、环保标准。",
    icon: "/src/assets/assets/svg/shop.svg"
  },
  {
    id: 3,
    name: "国家高新技术企业",
    description: "国家级高新技术企业认证，体现技术创新实力。",
    icon: "/src/assets/assets/svg/user_avatar.svg"
  }
])
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Bitter:wght@300;400;500;600;700&family=Hind:wght@300;400;500;600;700&display=swap');

.quality-page {
  font-family: 'Hind', 'Bitter', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: #333;
  overflow-x: hidden;
}

.hero-section {
  position: relative;
  color: white;
  padding: 120px 0 80px;
  text-align: center;
  margin-top: 75px;
}

.hero-background {
  position: absolute;
  inset: 0;
  z-index: -1;
}

.hero-background img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.slide-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4));
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 50px;
}

.page-title {
  font-size: 58px;
  font-weight: 700;
  margin-bottom: 28px;
  line-height: 1.2;
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  font-family: 'Bitter', serif;
  letter-spacing: -0.5px;
}

.page-subtitle {
  font-size: 20px;
  margin-bottom: 45px;
  opacity: 0.95;
  font-weight: 300;
  line-height: 1.7;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  font-family: 'Hind', sans-serif;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.content-section {
  padding: 90px 0;
  background-color: #f5f6f8;
}

.quality-intro {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-size: 36px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 20px;
  font-family: 'Bitter', serif;
}

.section-description {
  font-size: 18px;
  color: #7f8c8d;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
  font-family: 'Hind', sans-serif;
}

.quality-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-bottom: 80px;
}

.quality-card {
  position: relative;
  height: 340px;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  background-size: cover;
  background-position: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.quality-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
}

.card-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.75));
  transition: all 0.4s ease;
}

.quality-card:hover .card-overlay {
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.85));
}

.card-content {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  padding: 35px;
  text-align: center;
}

.quality-icon {
  width: 80px;
  height: 80px;
  margin-bottom: 24px;
  background: rgba(217, 127, 62, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.95;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
}

.quality-card:hover .quality-icon {
  transform: scale(1.15) translateY(-5px);
}

.quality-icon img {
  width: 40px;
  height: 40px;
  filter: brightness(0) invert(1);
}

.quality-title {
  color: white;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  text-shadow: 0 3px 12px rgba(0, 0, 0, 0.4);
  line-height: 1.4;
  font-family: 'Bitter', serif;
  letter-spacing: 0.2px;
  margin-bottom: 15px;
  transition: transform 0.3s;
}

.quality-card:hover .quality-title {
  transform: translateY(-3px);
}

.quality-description {
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  line-height: 1.6;
  font-weight: 300;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  font-family: 'Hind', sans-serif;
}

.certifications {
  margin-top: 60px;
}

.cert-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.cert-item {
  background: white;
  border-radius: 16px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease;
}

.cert-item:hover {
  transform: translateY(-5px);
}

.cert-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cert-icon img {
  width: 30px;
  height: 30px;
  filter: brightness(0) invert(1);
}

.cert-name {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 10px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Roboto', sans-serif;
}

.cert-description {
  font-size: 14px;
  color: #7f8c8d;
  line-height: 1.5;
}

@media (max-width: 1024px) {
  .quality-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 28px;
  }

  .container {
    padding: 0 30px;
  }
}

@media (max-width: 768px) {
  .container {
    padding: 0 20px;
  }

  .hero-section {
    padding: 100px 0 60px;
    margin-top: 65px;
  }

  .content-section {
    padding: 70px 0;
  }

  .quality-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .quality-card {
    height: 300px;
  }

  .page-title {
    font-size: 38px;
  }

  .page-subtitle {
    font-size: 17px;
    margin-bottom: 35px;
  }

  .section-title {
    font-size: 28px;
  }
  
  .section-description {
    font-size: 16px;
  }
  
  .cert-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}
</style>